import { createFileRoute } from '@tanstack/react-router';
import { useActiveOrganizationId, useActiveRealmAPIContext } from '@interxfi/api-client';
import { PageLayout } from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { RequiresAccess } from '@interxfi/design-system/access-context';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { Button } from '@interxfi/design-system/components/button/button';
import type { CustomTableHeaderProps } from '@interxfi/design-system/components/table';
import PlusIcon from '@interxfi/design-system/icons/react/icon-plus';
import { type PortalTabbedNavigationTab, TabbedNavigationGroup } from '../../../../../components/router/router-tabbed-navigation';
import { useCanActAsOrganizationAdmin } from '../../../../../features/userauth/organization/userauth-organization-permission-helpers';
import { UserauthOrganizationMembersTable } from '../../../../../features/userauth/organization-member/userauth-organization-members-table';
import { UserauthOrganizationMemberInvitationCreateDrawer } from '../../../../../features/userauth/organization-member-invitation/userauth-organization-member-invitation-create-drawer';
import { PlatformUsersTabRail } from '../../../../../features/userauth/platform-users-tab-rail';
import {
  UserauthPlatformUsersTable,
  type UserauthPlatformUsersTableHeaderActionProps,
} from '../../../../../features/userauth/user/userauth-platform-users-table';
import { getPageHead } from '../../../../../util/routing/meta';

export const Route = createFileRoute('/_app/_platform/_context-required/user/')({
  staticData: {
    tab: {
      group: TabbedNavigationGroup.PlatformUsers,
      label: 'Members',
      order: 0,
    },
  } satisfies PortalTabbedNavigationTab<TabbedNavigationGroup.PlatformUsers>,
  loader: () =>
    ({
      breadcrumb: buildBreadcrumb({
        id: 'user-membership-list',
        label: 'Users',
        to: '/user',
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('Members'),
  component: UserOrganizationMembershipList,
});

function PlatformTableHeader({ filter, search }: UserauthPlatformUsersTableHeaderActionProps) {
  return (
    <VerticalStack gap={7}>
      <PlatformUsersTabRail
        actions={
          <HorizontalStack align="end" blockAlign="center" gap={2} wrap="nowrap">
            {filter}
          </HorizontalStack>
        }
      />

      {search}
    </VerticalStack>
  );
}

function TableHeader({ filter, search }: CustomTableHeaderProps) {
  const activeOrganizationId = useActiveOrganizationId();
  const canActAsAdmin = useCanActAsOrganizationAdmin(activeOrganizationId);

  return (
    <VerticalStack gap={7}>
      <PlatformUsersTabRail
        actions={
          <HorizontalStack blockAlign="center" gap={2} wrap="nowrap">
            {canActAsAdmin ? (
              <RequiresAccess write>
                <UserauthOrganizationMemberInvitationCreateDrawer
                  organizationId={activeOrganizationId}
                  trigger={
                    <Button affixIcon={<PlusIcon aria-hidden />} aria-label="Send an organization invitation" size="small" variant="primary" />
                  }
                />
              </RequiresAccess>
            ) : null}

            {filter}
          </HorizontalStack>
        }
      />

      {search}
    </VerticalStack>
  );
}

function UserOrganizationMembershipList() {
  const activeOrganizationId = useActiveOrganizationId();
  const { activeTenantIsPlatform } = useActiveRealmAPIContext();

  return (
    <PageLayout>
      <PageLayout.Section noBorder>
        {activeTenantIsPlatform ? (
          <UserauthPlatformUsersTable HeaderActions={PlatformTableHeader} />
        ) : (
          <UserauthOrganizationMembersTable HeaderActions={TableHeader} organizationId={activeOrganizationId} />
        )}
      </PageLayout.Section>
    </PageLayout>
  );
}
